import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Spinner } from "../components/ui/shadcn-io/spinner";
import { HolidayCalendar } from "../components/holidayCalendar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";

interface Holiday {
  _id: string;
  date: string;
  name: string;
  year?: number;
  state?: string;
  isActive?: boolean;
}

export default function EmployeeHolidays() {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedHoliday, setSelectedHoliday] = useState<Holiday | null>(null);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [allYearHolidays, setAllYearHolidays] = useState<Holiday[]>([]);

  useEffect(() => {
    fetchHolidays();
  }, [selectedYear, currentDate]);

  const fetchHolidays = async () => {
    try {
      setIsLoading(true);
      setError("");
      const token = localStorage.getItem("authToken");
      const year = currentDate.getFullYear();
      const month = currentDate.getMonth() + 1; // getMonth() returns 0-11, API expects 1-12
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/holidays?year=${year}&month=${month}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setHolidays(
        Array.isArray(response.data)
          ? response.data
          : response.data?.holidays || []
      );
    } catch (err: any) {
      console.error("Error fetching holidays:", err);
      setError(
        err.response?.data?.message || "Failed to fetch public holidays"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAllYearHolidays = async (year: number) => {
    try {
      const token = localStorage.getItem("authToken");
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}api/holidays?year=${year}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setAllYearHolidays(
        Array.isArray(response.data)
          ? response.data
          : response.data?.holidays || []
      );
    } catch (err: any) {
      console.error("Error fetching all year holidays:", err);
    }
  };

  useEffect(() => {
    fetchAllYearHolidays(selectedYear);
  }, [selectedYear]);

  const handleDateClick = (date: Date, holiday?: Holiday) => {
    if (holiday) {
      setSelectedHoliday(holiday);
      setIsViewDialogOpen(true);
    }
  };

  const activeHolidays = holidays.filter((h) => h.isActive !== false);
  const allYearActiveHolidays = allYearHolidays.filter(
    (h) => h.isActive !== false
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-2">
          <Spinner variant="ellipsis" size={40} />
          <p className="text-sm text-muted-foreground">Loading holidays...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-6 py-8">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Public Holidays</h1>
        <p className="text-muted-foreground mt-1">
          View public holidays calendar
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800 font-medium">{error}</p>
        </div>
      )}

      {/* Year Selector */}
      <div className="mb-6">
        <Select
          value={selectedYear.toString()}
          onValueChange={(value) => {
            const newYear = parseInt(value);
            setSelectedYear(newYear);
            // Navigate to January of the selected year
            setCurrentDate(new Date(newYear, 0, 1));
            fetchAllYearHolidays(newYear);
          }}
        >
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Select year" />
          </SelectTrigger>
          <SelectContent>
            {Array.from({ length: 5 }, (_, i) => {
              const year = new Date().getFullYear() - 1 + i;
              return (
                <SelectItem key={year} value={year.toString()}>
                  {year}
                </SelectItem>
              );
            })}
          </SelectContent>
        </Select>
      </div>

      {/* Calendar View */}
      <Card>
        <CardHeader>
          <CardTitle>Holiday Calendar</CardTitle>
        </CardHeader>
        <CardContent>
          <HolidayCalendar
            holidays={activeHolidays}
            currentDate={currentDate}
            onDateChange={setCurrentDate}
            onDateClick={handleDateClick}
            readOnly={true}
          />
        </CardContent>
      </Card>

      {/* View Holiday Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Holiday Details</DialogTitle>
            <DialogDescription>
              Information about the public holiday
            </DialogDescription>
          </DialogHeader>
          {selectedHoliday && (
            <div className="space-y-4">
              <div>
                <Label className="text-muted-foreground">Holiday Name</Label>
                <p className="font-medium mt-1">{selectedHoliday.name}</p>
              </div>
              <div>
                <Label className="text-muted-foreground">Date</Label>
                <p className="font-medium mt-1">
                  {new Date(selectedHoliday.date).toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </p>
              </div>
              {selectedHoliday.state && (
                <div>
                  <Label className="text-muted-foreground">State</Label>
                  <div className="mt-1">
                    <Badge variant="outline">{selectedHoliday.state}</Badge>
                  </div>
                </div>
              )}
              <div>
                <Label className="text-muted-foreground">Year</Label>
                <p className="font-medium mt-1">
                  {selectedHoliday.year ||
                    new Date(selectedHoliday.date).getFullYear()}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsViewDialogOpen(false)}
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
